import * as Api from './api';
import * as DocPartnerApi from 'features/document/edit/partners/api';
import * as OfferAssetApi from 'features/offer/edit/assets/asset/api';
import * as actionTypes from './actionTypes';
import * as eventTypes from './eventTypes';
import { sendHttpCommandAndWaitForEvent, sendHttpRequest } from 'actions/actionCreators/httpActions';
import { showError } from 'actions/actionCreators/snackbarActions';
import { buildDownloadUrl } from 'features/document/edit/documents/actionCreators';
import { getPartnerIsLoaded } from './selectors';
import { camelizeKeys } from 'humps';

export function loadDocumentPartner(documentId, siteId, partnerId, documentTypeId) {
  return async (dispatch, getState) => {
    if (!(documentId || siteId) || !partnerId || getPartnerIsLoaded(getState(), partnerId)) return;

    try {
      dispatch(sendHttpRequest(DocPartnerApi.loadDocumentPartner(documentId, siteId, partnerId, documentTypeId))).then(partner =>
        dispatch({ type: actionTypes.LOAD_OFFER_PARTNER_SUCCEEDED, payload: { partnerId, partner } })
      );
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadDocumentAssociatedPartners(documentId, siteId) {
  return async (dispatch, getState) => {
    if (!(documentId || siteId)) return;
    const key = `${documentId}-${siteId}`;
    const associatedPartners = getState().currentOffer.associatedPartners[key];
    if (associatedPartners?.loaded || associatedPartners?.loading) return;

    try {
      dispatch(sendHttpRequest(DocPartnerApi.loadDocumentAssociatedPartners(documentId, siteId))).then(associatedPartners =>
        dispatch({
          type: actionTypes.LOAD_OFFER_ASSOCIATED_PARTNERS_SUCCEEDED,
          key: key,
          payload: associatedPartners
        })
      );
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}
export function loadPartnerAdresses(documentId, siteId, partnerId) {
  return async (dispatch, getState) => {
    const clientAddresses = getState().currentOffer.clientAddressesData[partnerId];
    let shouldCallApi = !clientAddresses || (!clientAddresses.loaded && !clientAddresses.loading);
    if (!shouldCallApi) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_CLIENT_ADDRESSES_STARTED, key: partnerId });

    try {
      const response = await dispatch(sendHttpRequest(DocPartnerApi.getPartnerAddresses(documentId, siteId, partnerId)));
      const result = camelizeKeys(response);
      await dispatch({
        type: actionTypes.LOAD_CLIENT_ADDRESSES_SUCCEEDED,
        key: partnerId,
        addresses: result
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_CLIENT_ADDRESSES_FAILED, key: partnerId, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function togglePartnerFormCard(partnerId, isAssociatedPartner) {
  return dispatch => {
    dispatch({ type: actionTypes.TOGGLE_OFFER_PARTNER_FORM_CARD, payload: { partnerId, isAssociatedPartner } });
  };
}

export function loadPartnerByUniqueId(documentId, siteId, uniqueId, partnerId, partnerType, callbackFn = () => {}) {
  return async dispatch => {
    try {
      dispatch({
        type: actionTypes.LOAD_OFFER_PARTNER_BY_ID_STARTED,
        payload: { partnerId: partnerId }
      });

      if (partnerType.typeCheck.isPF) {
        await dispatch(loadPartner(documentId, siteId, uniqueId, partnerType));
        dispatch({
          type: actionTypes.LOAD_OFFER_PARTNER_BY_ID_SUCCEEDED,
          payload: { partnerId: partnerId, uniqueId: uniqueId }
        });
      } else if (partnerType.typeCheck.isPJ || partnerType.typeCheck.isPFA) {
        await dispatch(getCompanyInformation(uniqueId, partnerType.partnerTypeId, callbackFn)).then(partner => {
          dispatch(loadPartnerHierarchy(partner?.eventBody?.partnerGroupId));
          dispatch({
            type: actionTypes.LOAD_OFFER_PARTNER_BY_ID_SUCCEEDED,
            payload: { partnerId: partnerId, uniqueId: partner?.eventBody?.uniqueId ?? uniqueId }
          });
        });
      }
    } catch (error) {
      dispatch({
        type: actionTypes.LOAD_OFFER_PARTNER_BY_ID_FAILED,
        payload: { partnerId: partnerId }
      });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function getNationalityByUniqueId(uniqueId, isExternal) {
  return async dispatch => {
    try {
      const data = await dispatch(sendHttpRequest(Api.getPartnerNationalityId(uniqueId, isExternal)));
      if (data) {
        await dispatch({
          type: actionTypes.LOAD_NATIONALITY_ID_BY_UNIQUE_ID_SUCCEDEED,
          payload: { uniqueId: uniqueId.replace(/\s/g, ''), nationalityId: data.nationalityId }
        });
        return data.nationalityId;
      }
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function getCompanyInformation(uniqueId, partnerTypeId, callbackFn) {
  return async dispatch => {
    const client = await dispatch(
      sendHttpCommandAndWaitForEvent(
        Api.getCompanyInformation(uniqueId, partnerTypeId),
        'GetCompanyInformationCommand',
        [eventTypes.OFFER_COMPANY_INFO_RETRIEVED, eventTypes.OFFER_COMPANY_INFO_NOT_FOUND],
        false
      )
    );
    if (client.eventType === eventTypes.OFFER_COMPANY_INFO_RETRIEVED)
      dispatch({
        type: actionTypes.GET_COMPANY_INFO_SUCCEEDED,
        payload: {
          partnerId: client.eventBody.partnerId,
          uniqueId: client.eventBody.uniqueId ?? uniqueId,
          masterUniqueId: client.eventBody.uniqueId,
          partnerName: client.eventBody.partnerName,
          comercialRegistration: client.eventBody.comercialRegistration,
          dealerId: client.eventBody.dealerId,
          legalFormId: client.eventBody.legalFormId,
          registrationDate: client.eventBody.registrationDate,
          contactData: client.eventBody.contactData,
          contactMode: client.eventBody.contactMode,
          addressData: client.eventBody.addressesData,
          partnerGroupId: client.eventBody.partnerGroupId,
          dealerContactInfos: client.eventBody.dealerContactInfos
        }
      });

    callbackFn(uniqueId, client.eventBody.partnerName);
    return client;
  };
}

export function loadPartner(documentId, siteId, uniqueId, partnerType, isExternal = false) {
  return async (dispatch, getState) => {
    if (!uniqueId || !partnerType) {
      return;
    }

    const reduxClient = getState().clientData.clients.byUniqueId[uniqueId];
    if (reduxClient && reduxClient.found) {
      return reduxClient;
    }

    const client = await dispatch(sendHttpRequest(Api.getClient(documentId, siteId, uniqueId, partnerType.partnerTypeId, isExternal)));
    if (client) {
      await dispatch({
        type: actionTypes.LOAD_CLIENT_BY_ID_SUCCEEDED,
        payload: { uniqueId: uniqueId.replace(/\s/g, ''), client: client }
      });
    }
    return client;
  };
}

export function saveOfferPartnerAddresses(documentId, siteId, partnerId, partnerAddresses) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.SAVE_OFFER_PARTNER_ADDRESSES_STARTED });
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          DocPartnerApi.updateOfferClientAddresses(documentId, siteId, partnerId, partnerAddresses),
          'RunningOfferCommand',
          [eventTypes.OFFER_PARTNER_ADDRESSES_UPDATED]
        )
      );

      if (event.ok) {
        dispatch({
          type: actionTypes.SAVE_OFFER_PARTNER_ADDRESSES_SUCCEEDED,
          key: partnerId
        });
      } else {
        dispatch({ type: actionTypes.SAVE_OFFER_PARTNER_ADDRESSES_FAILED });
      }
    } catch (error) {
      dispatch({ type: actionTypes.SAVE_OFFER_PARTNER_ADDRESSES_FAILED });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function saveOfferPartner(documentId, siteId, partnerId, partner, isAssociatedPartner) {
  return async (dispatch, getState) => {
    try {
      const config = getState().offerConfig;
      const documentPartnerAllowedRoles = getState().params.items.documentPartnerAllowedRoles.split(';');
      dispatch({ type: actionTypes.SAVE_OFFER_PARTNER_STARTED });

      const ajaxProm = isAssociatedPartner
        ? Api.updateOfferAssociatedPartnerData(
            createAssociatedPartnerRequestBody(documentId, siteId, partner, partnerId, config, documentPartnerAllowedRoles)
          )
        : Api.updateOfferClientData(createClientRequestBody(documentId, siteId, partner, partnerId, config));

      const event = await dispatch(sendHttpCommandAndWaitForEvent(ajaxProm, 'RunningOfferCommand', [eventTypes.OFFER_PARTNER_UPDATED]));

      if (event.ok) {
        const partnerIdFromEvent = event.eventBody.partnerId;
        await dispatch({
          type: actionTypes.SAVE_OFFER_PARTNER_SUCCEEDED,
          payload: {
            partnerId: partnerIdFromEvent,
            partner: { ...partner, generalData: { ...partner.generalData, uniqueId: event.eventBody.uniqueId } }
          }
        });
      } else {
        dispatch({ type: actionTypes.SAVE_OFFER_PARTNER_FAILED });
      }
    } catch (error) {
      dispatch({ type: actionTypes.SAVE_OFFER_PARTNER_FAILED });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadPFPartners(filters) {
  return async dispatch => {
    dispatch({ type: actionTypes.LOAD_PF_PARTNERS_STARTED });
    try {
      let partners = await dispatch(sendHttpRequest(Api.loadPFPartners(filters)));
      dispatch({ type: actionTypes.LOAD_PF_PARTNERS_SUCCEEDED, payload: { partners } });
      return partners;
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_PF_PARTNERS_FAILED, payload: { error } });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadLegalPartners(filters) {
  return async dispatch => {
    dispatch({ type: actionTypes.LOAD_PJ_PARTNERS_STARTED });
    try {
      let partners = await dispatch(sendHttpRequest(Api.loadLegalPartners(filters)));
      dispatch({ type: actionTypes.LOAD_PJ_PARTNERS_SUCCEEDED });
      return partners;
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_PJ_PARTNERS_FAILED });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function deleteAssociatedPartner(data) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.DELETE_OFFER_ASSOCIATED_PARTNER_STARTED });
      await dispatch(
        sendHttpCommandAndWaitForEvent(Api.deleteAssociatedPartner(data), 'RunningOfferCommand', [
          eventTypes.OFFER_ASSOCIATED_PARTNER_DELETED
        ])
      );
    } catch (error) {
      dispatch({ type: actionTypes.DELETE_OFFER_ASSOCIATED_PARTNER_FAILED });
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function deleteDocumentPartner(data) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.DELETE_OFFER_DOCUMENT_PARTNER_STARTED });
      await dispatch(
        sendHttpCommandAndWaitForEvent(Api.deleteDocumentPartner(data), 'RunningOfferCommand', [eventTypes.OFFER_PARTNER_RESET])
      );
    } catch (error) {
      dispatch({ type: actionTypes.DELETE_OFFER_DOCUMENT_PARTNER_FAILED });
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadClientByIdCard(file, partnerId, documentId, siteId, updateProgress = () => {}) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.LOAD_CLIENT_BY_CARD_STARTED, payload: { partnerId } });
      const event = await dispatch(
        uploadOCRFile(documentId, siteId, partnerId, { file }, progress => {
          updateProgress(progress);
        })
      );
      if (event.ok) {
        dispatch({
          type: actionTypes.LOAD_CLIENT_BY_CARD_SUCCEEDED,
          payload: {
            partnerId,
            uniqueId: event.eventBody.generalData.uniqueId,
            partnerType: event.eventBody.partnerType
          }
        });
      } else {
        dispatch({ type: actionTypes.LOAD_CLIENT_BY_CARD_FAILED, payload: { partnerId } });
      }
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      dispatch({ type: actionTypes.LOAD_CLIENT_BY_CARD_FAILED, payload: { partnerId } });
    }
  };
}

export function uploadOCRFile(documentId, siteId, partnerId, formData, progressCallback) {
  return async dispatch => {
    try {
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(Api.uploadOCRFile(documentId, siteId, partnerId, formData, progressCallback), 'UploadDocument', [
          eventTypes.OFFER_OCR_FILE_UPLOADED
        ])
      );

      return event;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadDefaultPartnerAddress(partnerTypeId) {
  return async (dispatch, getState) => {
    if (!partnerTypeId) return;

    let defaultAddress = getState().clientData.defaultAddress[partnerTypeId];
    if (defaultAddress) return;

    try {
      dispatch({ type: actionTypes.LOAD_DEFAULT_PARTNER_ADDRESS_STARTED, partnerTypeId });
      defaultAddress = await dispatch(sendHttpRequest(Api.getDefaultPartnerAddress(partnerTypeId)));
      dispatch({ type: actionTypes.LOAD_DEFAULT_PARTNER_ADDRESS_SUCCEEDED, payload: defaultAddress, partnerTypeId });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function setDefaultPartnerAddress(partnerId = null, uniqueId = null, defaultAddress) {
  return async dispatch => {
    if (!uniqueId && partnerId !== 0 && !partnerId) return;

    if (uniqueId) dispatch({ type: actionTypes.SET_DEFAULT_ADDRESS_ON_CLIENT, defaultAddress, uniqueId });

    if (partnerId === 0 || partnerId) dispatch({ type: actionTypes.SET_DEFAULT_ADDRESS_ON_OFFER_CLIENT, defaultAddress, partnerId });
  };
}

export function finishPartnerQuery() {
  return dispatch => {
    dispatch({ type: actionTypes.FINISH_PARTNER_QUERY });
  };
}

export function setBirthDateFromCNP(uniqueId = null, birthDate) {
  return async dispatch => {
    if (!uniqueId) return;
    dispatch({ type: actionTypes.SET_BIRTH_DATE_ON_CLIENT, birthDate, uniqueId });
  };
}

export function loadCounties(search, countryId) {
  return async (dispatch, getState) => {
    const countiesLoaded = getState().clientData.counties.byCountryId[countryId]?.loaded || false;
    const countiesLoading = getState().clientData.counties.byCountryId[countryId]?.loading || false;

    if (!countryId || countiesLoaded || countiesLoading) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_COUNTIES_STARTED, payload: { countryId } });

    try {
      const counties = await dispatch(sendHttpRequest(Api.getCounties(search, countryId)));
      dispatch({ type: actionTypes.LOAD_COUNTIES_SUCCEEDED, payload: { countryId, counties } });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadPartnerTypes() {
  return async (dispatch, getState) => {
    let { partnerTypes } = getState().clientData;
    if (partnerTypes.isLoaded) return partnerTypes;

    try {
      partnerTypes = await dispatch(sendHttpRequest(Api.getPartnerTypes()));
      dispatch({ type: actionTypes.LOAD_PARTNER_TYPES_SUCCEEDED, payload: { partnerTypes } });
      return partnerTypes;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadIdTypes() {
  return async (dispatch, getState) => {
    let { idTypes } = getState().clientData;
    if (idTypes.isLoaded) return idTypes;

    try {
      idTypes = await dispatch(sendHttpRequest(Api.getIdTypes()));
      dispatch({ type: actionTypes.LOAD_ID_TYPES_SUCCEEDED, payload: { idTypes } });
      return idTypes;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadPartnerRoles(documentTypeId) {
  return async (dispatch, getState) => {
    let { partnerRoles } = getState().clientData;
    const filteredPartnerRoles = partnerRoles?.filter(x => x.documentTypeId === documentTypeId);
    if (partnerRoles.isLoaded && filteredPartnerRoles.length > 0) return filteredPartnerRoles;

    try {
      partnerRoles = await dispatch(sendHttpRequest(Api.getPartnerRoles(documentTypeId)));
      partnerRoles.forEach(x => (x.documentTypeId = documentTypeId));
      dispatch({ type: actionTypes.LOAD_PARTNER_ROLES_SUCCEEDED, payload: { partnerRoles } });
      return partnerRoles;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadCountries(search) {
  return async (dispatch, getState) => {
    let { countries } = getState().clientData;
    if (countries.isLoaded) return countries;

    try {
      countries = await dispatch(sendHttpRequest(Api.getCountries(search)));
      dispatch({ type: actionTypes.LOAD_COUNTRIES_SUCCEEDED, payload: { countries } });
      return countries;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadCitizenships(search) {
  return async (dispatch, getState) => {
    let { citizenships } = getState().clientData;
    if (citizenships.isLoaded) return citizenships;

    try {
      citizenships = await dispatch(sendHttpRequest(Api.getCitizenships(search)));
      dispatch({ type: actionTypes.LOAD_CITIZENSHIPS_SUCCEEDED, payload: { citizenships } });
      return citizenships;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadRelations(search = null) {
  return async (dispatch, getState) => {
    let { relations } = getState().clientData;
    if (relations.isLoaded) return relations;

    try {
      relations = await dispatch(sendHttpRequest(Api.getRelations(search)));
      dispatch({ type: actionTypes.LOAD_RELATIONS_SUCCEEDED, payload: { relations } });
      return relations;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadGDPR() {
  return async dispatch => {
    try {
      let gdpr = await dispatch(sendHttpRequest(Api.getGDPR()));
      dispatch({ type: actionTypes.LOAD_GDPR_SUCCEEDED, payload: { gdpr } });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadCities(search, countyId) {
  return async dispatch => {
    try {
      const cities = await dispatch(sendHttpRequest(Api.getCities(search, countyId)));
      return cities;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function onPrintGDPR(documentId, siteId, partnerId, isAssociatedPartner) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.PRINTING_GDPR_STARTED, payload: { partnerId, isAssociatedPartner } });
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          Api.printGDPR(documentId, siteId, partnerId),
          'RunningOfferCommand',
          [eventTypes.GDPR_DOWNLOADED],
          false
        )
      );
      if (event.ok) {
        dispatch(handleDownload(documentId, siteId, event.eventBody.filePath, event.eventBody.fileName)).then(
          dispatch({ type: actionTypes.PRINTING_GDPR_SUCCEEDED, payload: { partnerId, isAssociatedPartner } })
        );
      } else {
        dispatch({ type: actionTypes.PRINTING_GDPR_FAILED });
      }
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

function handleDownload(documentId, siteId, filePath, fileName) {
  return async dispatch => {
    try {
      const link = dispatch(buildDownloadUrl(documentId, siteId, filePath, fileName));
      window.location = link;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function cancelOfferClientChanges(documentId, siteId, partnerId, isAssociatedPartner) {
  return dispatch => {
    return dispatch({
      type: actionTypes.CANCEL_OFFER_CLIENT_CHANGES,
      payload: { documentId, siteId, partnerId, isAssociatedPartner }
    });
  };
}

function createClientRequestBody(documentId, siteId, client, partnerId, config) {
  const { generalData, addresses, partnerType } = client;

  const clientAddresses = JSON.parse(JSON.stringify(addresses));
  clientAddresses.forEach(address => {
    address.countryId = address.country ? address.country.id : null;
    address.countyId = address.county ? address.county.id : null;
    address.cityId = address.city ? address.city.id : null;
  });

  return {
    ...client,
    documentId,
    siteId,
    partnerId: partnerId || null,
    generalData: {
      ...generalData,
      relationId: generalData.relationId || config.defaultRelationId,
      isAssociatedDocumentPartner: false
    },
    partnerType: {
      partnerTypeId: partnerType.partnerTypeId || config.defaultPartnerType.partnerTypeId,
      partnerTypeCode: partnerType.partnerTypeCode || config.defaultPartnerType.partnerTypeCode,
      partnerTypeName: partnerType.partnerTypeName || config.defaultPartnerType.partnerTypeName
    },
    addresses: clientAddresses
  };
}

function createAssociatedPartnerRequestBody(documentId, siteId, partner, partnerId, config, documentPartnerAllowedRoles) {
  const { generalData, addresses } = partner;

  const associatedPartnerAddresses = JSON.parse(JSON.stringify(addresses));
  associatedPartnerAddresses.forEach(address => {
    if (address) {
      address.countryId = address.country ? address.country.id : null;
      address.countyId = address.county ? address.county.id : null;
      address.cityId = address?.city ? address?.city?.id : null;
    }
  });

  return {
    ...partner,
    documentId,
    siteId,
    partnerId: partnerId === 0 ? null : partnerId,
    generalData: {
      ...generalData,
      relationId: generalData.relationId || config.defaultRelationId,
      isAssociatedDocumentPartner: !documentPartnerAllowedRoles.includes(partner.partnerRoleCode)
    },
    addresses: associatedPartnerAddresses
  };
}

export function loadPartnerFromQuery(documentId, siteId, partnerInformation) {
  const uniqueId = partnerInformation.uniqueId;
  return async dispatch => {
    try {
      dispatch({
        type: actionTypes.LOAD_PARTNER_FROM_QUERY_PROGRESS_STARTED,
        payload: { isExternalVerificationInProgress: true, uniqueId }
      });
      const client = await dispatch(
        sendHttpCommandAndWaitForEvent(
          OfferAssetApi.retrievePartnerFromQuery(documentId, siteId, partnerInformation),
          'RetrievePartnerFromQueryCommand',
          [],
          false
        )
      );
      if (client.ok) {
        dispatch({
          type: actionTypes.LOAD_PARTNER_FROM_QUERY,
          payload: {
            uniqueId: uniqueId,
            partnerId: client.eventBody.partnerId,
            partnerName: client.eventBody.partnerName,
            comercialRegistration: client.eventBody.comercialRegistration,
            legalFormId: client.eventBody.legalFormId,
            registrationDate: client.eventBody.registrationDate,
            contactData: client.eventBody.contactData,
            contactMode: client.eventBody.contactMode,
            addressData: client.eventBody.addressesData
          }
        });
      } else {
        dispatch({
          type: actionTypes.LOAD_PARTNER_FROM_QUERY_PROGRESS_FAILED,
          payload: { isExternalVerificationInProgress: false, uniqueId }
        });
      }
    } catch (error) {
      dispatch({
        type: actionTypes.LOAD_PARTNER_FROM_QUERY_PROGRESS_FAILED,
        payload: { isExternalVerificationInProgress: false, uniqueId }
      });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadPartnerHierarchy(partnerGroupId) {
  return async (dispatch, getState) => {
    let { partnerHierarchies } = getState().clientData;
    if (partnerHierarchies && partnerHierarchies[partnerGroupId]) return partnerHierarchies[partnerGroupId];
    if (!partnerGroupId) return;

    try {
      var partnerHierarchy = await dispatch(sendHttpRequest(Api.getPartnerHierarchy(partnerGroupId)));
      dispatch({
        type: actionTypes.LOAD_PARTNER_HIERARCHY_SUCCEEDED,
        payload: { partnerHierarchy: partnerHierarchy, partnerGroupId: partnerGroupId }
      });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function saveAddedPartnersFromHierarchy(documentId, siteId, partnerGroupId, addedPartners, callback) {
  return async dispatch => {
    dispatch({ type: actionTypes.SAVE_ADDED_HIERARCHY_PARTNERS_STARTED, payload: { documentId, siteId } });
    dispatch(
      sendHttpCommandAndWaitForEvent(
        Api.savePartnerHierarchy(documentId, siteId, partnerGroupId, addedPartners),
        'SaveAddedPartnersFromHierarchy',
        [eventTypes.SAVE_ADDED_HIERARCHY_PARTNERS_UPDATED]
      )
    )
      .then(callback())
      .catch(error => {
        dispatch({
          type: actionTypes.SAVE_ADDED_HIERARCHY_PARTNERS_FAILED,
          payload: { documentId, siteId, error }
        });
        dispatch(showError(error.message, error.correlationId));
      });
  };
}

export function loadDefaultOCRFileExtensions() {
  return async (dispatch, getState) => {
    let { defaultOCRFileExtensions } = getState().clientData;
    if (defaultOCRFileExtensions.isLoaded) return defaultOCRFileExtensions;

    try {
      defaultOCRFileExtensions = await dispatch(sendHttpRequest(Api.getDefaultOCRFileExtensions()));
      dispatch({ type: actionTypes.LOAD_OCR_FILE_EXTENSIONS_SUCCEDED, payload: { defaultOCRFileExtensions } });
      return defaultOCRFileExtensions;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function refreshAddressesFinished(partnerId) {
  return async dispatch => {
    dispatch({ type: actionTypes.REFRESH_PARTNER_ADDRESSES_FINISHED, partnerId });
  };
}
